import React, { useState, useMemo, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import Button from "../../../shared/Button";
import {
  useAssessmentConfiguration,
  useYearLevelByAssessment,
} from "../../../../hooks/useAssessment";
import CustomModal from "../../../shared/CustomModal";
import AssessmentForm from "../create-assessment/AssessmentForm";
import { adToBsDate } from "../../../../../../utils/Utils";
import moment from "moment";
import AssessmentDetail from "./AssessmentDetail";
import SubjectConfiguration from "./SubjectConfiguration";
import { checkRolePermissions } from "../../../../../../utils/Constants";
import GeneralConfiguration from "./general-configuration/GeneralConfiguration";
import { Spinner } from "reactstrap";
import { Tooltip } from "@mui/material";

const Assessment = ({
  assessment,
  refetchAssessment,
  currentPage,
  isOpen,
  setIsOpen,
  name,
  pageSize,
  assessmentConfig,
  totalRecordsCount,
  setCurrentPage,
}) => {
  const [deleteModal, setDeleteModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [detailModal, setDetailModal] = useState(false);
  const [detailPayload, setDetailPayload] = useState(null);
  const [subjectConfigModal, setSubjectConfigModal] = useState(false);
  const [generalConfigModal, setGeneralConfigModal] = useState(false);
  const [aggregateWeightModal, setAggregateWeightModal] = useState(false);
  const [assessmentInfo, setAssessmentInfo] = useState({
    assessmentName: assessment.name || "",
    yearLevel: "",
    classroom: "",
  });
  const [permissions, setPermissions] = useState({
    permissionGeneralConfig: false,
    permissionSubjectConfig: false,
    permissionEditAssessment: false,
    permissionCancelAssessment: false,
  });

  const {
    assessmentConfiguration,
    loading: assessmentConfigurationLoading,
    getAssessmentConfiguration,
    updateAssessmentConfiguration,
    deleteAssessment,
  } = useAssessmentConfiguration();

  const { loading, yearLevelByAssessment, getYearLevelByAssessment } =
    useYearLevelByAssessment();

  const checkPermissions = () => {
    setPermissions({
      permissionGeneralConfig: checkRolePermissions(
        "general-assessment-config",
        "activity",
      ),
      permissionSubjectConfig: checkRolePermissions(
        "subject-assessment-config",
        "activity",
      ),
      permissionEditAssessment: checkRolePermissions(
        "edit-assessment",
        "activity",
        name,
      ),
      permissionCancelAssessment: checkRolePermissions(
        "cancel-assessment",
        "activity",
        name,
      ),
    });
  };

  //assessment configuration update handler
  const handleAssessmentUpdate = async (data) => {
    try {
      await updateAssessmentConfiguration(data);
      refetchAssessment(currentPage);
    } catch (error) {
      console.error("Update Failed: ", error);
    }
  };

  //delete assessment
  const handleDeleteAssessment = async () => {
    if (!assessment?.id) return;

    try {
      await deleteAssessment(assessment?.id);
      const updatedTotalRecordsCount = totalRecordsCount - 1;
      const totalPages = Math.ceil(updatedTotalRecordsCount / pageSize);
      let page = currentPage;
      if (page > 1 && page > totalPages) {
        page = page - 1;
        setCurrentPage(page);
      }
      refetchAssessment(page);
    } catch (error) {
      console.error("Delete Failed: ", error);
    }
  };

  //extarct name and id from year level
  const classes = useMemo(() => {
    if (!yearLevelByAssessment) return;
    return yearLevelByAssessment?.map((item) => ({
      value: item.id,
      label: item.name,
    }));
  }, [yearLevelByAssessment]);

  //extract section
  const sections = useMemo(() => {
    if (!activeTab) return;
    return yearLevelByAssessment?.find((item) => item.id === activeTab)
      ?.classrooms;
  }, [activeTab, yearLevelByAssessment]);

  //set first class as active
  useEffect(() => {
    if (yearLevelByAssessment?.length > 0) {
      setActiveTab(yearLevelByAssessment[0].id);
    }
  }, [yearLevelByAssessment]);

  useEffect(() => {
    checkPermissions();
  }, []);

  return (
    <>
      <div style={{ marginTop: "16px" }} className="arr-accordion">
        <div
          className="flex"
          style={{
            justifyContent: "space-between",
            cursor: "pointer",
          }}
          onClick={() => {
            if (isOpen === assessment?.id) {
              setIsOpen(null);
              return;
            }
            getYearLevelByAssessment(assessment?.id);
            setIsOpen(assessment?.id);
          }}
        >
          <p style={{ margin: 0 }}>
            {assessment?.assessmentName} - {assessment?.assessmentTypeCode}
          </p>

          <div className="action-btns">
            <div className="action-btns">
              {assessment?.aggregationWeightConfiguration?.length > 0 && (
                <Button
                  onClick={() => setAggregateWeightModal(true)}
                  size={"sm"}
                  variant="outline"
                >
                  Aggregation Weight Configuration
                </Button>
              )}

              {permissions.permissionEditAssessment && (
                <Button
                  size="sm"
                  onClick={() => {
                    setUpdateModal((prev) => !prev);
                    getAssessmentConfiguration(assessment?.id);
                  }}
                >
                  Update
                </Button>
              )}

              {permissions.permissionCancelAssessment && (
                <Tooltip
                  title={
                    assessment?.isRecorded
                      ? "You cannot delete this assessment because student marks has already been recorded for this assessment."
                      : ""
                  }
                >
                  <div>
                    <Button
                      onClick={() => {
                        setDeleteModal(true);
                      }}
                      size="sm"
                      variant={"danger"}
                      disabled={assessment?.isRecorded}
                    >
                      {"Delete"}
                    </Button>
                  </div>
                </Tooltip>
              )}
            </div>
            {loading ? (
              <Spinner size={"sm"} color="primary" />
            ) : (
              <FontAwesomeIcon fontSize={14} icon={faChevronDown} />
            )}
          </div>
        </div>

        {isOpen === assessment?.id &&
          !assessment?.isDeleted &&
          yearLevelByAssessment?.length > 0 && (
            <>
              <div className="arr-tabs" style={{ marginTop: "24px" }}>
                {classes?.map((item) => (
                  <Button
                    key={item.value}
                    onClick={() => setActiveTab(item.value)}
                    size="sm"
                    variant={activeTab === item.value ? "primary" : "outline"}
                  >
                    Class {item.label}
                  </Button>
                ))}
              </div>

              {activeTab && sections && (
                <div className="arr-table-container">
                  <table className="arr-table">
                    <thead>
                      <tr>
                        <th>Section</th>
                        <th>Period</th>
                        <th>Settings</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sections?.map((item) => (
                        <tr key={item.id}>
                          <td style={{ whiteSpace: "nowrap" }}>{item?.name}</td>
                          <td>
                            <div
                              style={{
                                flexWrap: "nowrap",
                                whiteSpace: "nowrap",
                              }}
                              className="flex"
                            >
                              <span>
                                {adToBsDate(
                                  moment(item?.assessmentFromDate).format(
                                    "YYYY-MM-DD",
                                  ),
                                )}
                              </span>
                              <span>to</span>
                              <span>
                                {adToBsDate(
                                  moment(item?.assessmentToDate).format(
                                    "YYYY-MM-DD",
                                  ),
                                )}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div
                              style={{ flexWrap: "nowrap" }}
                              className="flex"
                            >
                              {item?.hasAssessmentGeneralConfiguration &&
                                permissions.permissionGeneralConfig && (
                                  <Button
                                    onClick={() => {
                                      setGeneralConfigModal(true);
                                      setDetailPayload({
                                        assessmentId: assessment.id,
                                        educationLevelDivisionId:
                                          assessment.educationLevelDivisionId,
                                        educationLevelId:
                                          assessment.educationLevelId,
                                        classroomId: item.id,
                                        yearLevelId: item.yearLevelId,
                                      });
                                    }}
                                    size="sm"
                                  >
                                    General Configuration
                                  </Button>
                                )}
                              {item?.hasAssessmentSubjectConfiguration &&
                                permissions.permissionSubjectConfig && (
                                  <Button
                                    onClick={() => {
                                      setSubjectConfigModal(true);
                                      setAssessmentInfo({
                                        assessmentName:
                                          assessment?.assessmentName,
                                        yearLevel: item?.yearLevelName,
                                        classroom: item.name,
                                      });
                                      setDetailPayload({
                                        assessmentId: assessment.id,
                                        educationLevelDivisionId:
                                          assessment.educationLevelDivisionId,
                                        educationLevelId:
                                          assessment.educationLevelId,
                                        classroomId: item.id,
                                        yearLevelId: item.yearLevelId,
                                      });
                                    }}
                                    size="sm"
                                  >
                                    Subject Configuration
                                  </Button>
                                )}
                            </div>
                          </td>
                          <td>
                            <Button
                              onClick={() => {
                                setDetailModal(true);
                                setDetailPayload({
                                  assessmentId: assessment.id,
                                  educationLevelDivisionId:
                                    assessment.educationLevelDivisionId,
                                  educationLevelId: assessment.educationLevelId,
                                  classroomId: item.id,
                                  yearLevelId: item.yearLevelId,
                                });
                              }}
                              variant="outline"
                              size="sm"
                            >
                              View Details
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
      </div>

      {/* delete modal */}
      <CustomModal
        isOpen={deleteModal}
        toggle={() => setDeleteModal(false)}
        modalHeader={"Are you sure?"}
        children={
          <div>
            <p>Once Deleted, you will not be able to recover this record</p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                gap: "8px",
              }}
            >
              <Button variant="outline" onClick={() => setDeleteModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={loading.delete}
                onClick={() => {
                  handleDeleteAssessment();
                  setDeleteModal(false);
                }}
              >
                {assessmentConfigurationLoading.delete ? (
                  <Spinner size={"sm"} color="white" />
                ) : (
                  "Delete"
                )}
              </Button>
            </div>
          </div>
        }
      />

      {/* Aggregate Weight modal */}
      <CustomModal
        isOpen={aggregateWeightModal}
        toggle={() => setAggregateWeightModal(false)}
        modalHeader={"Aggregate Weight Configuration"}
        children={
          <div style={{ margin: 0 }} className="arr-table-container">
            <table className="arr-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Assessment Name</th>
                  <th>Weight(%)</th>
                </tr>
              </thead>
              <tbody>
                {assessment?.aggregationWeightConfiguration?.map(
                  (item, index) => (
                    <tr key={item?.sourceAssessment}>
                      <td>{index + 1}</td>
                      <td>
                        {
                          assessmentConfig[item?.sourceAssessment]
                            ?.assessmentName
                        }
                      </td>
                      <td>{item?.percentageWeight}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        }
      />

      {/* Update Assessment modal */}
      <CustomModal
        size="lg"
        isOpen={updateModal}
        toggle={() => setUpdateModal((prev) => !prev)}
        modalHeader={"Edit Assessment"}
        children={
          <AssessmentForm
            isFetching={assessmentConfigurationLoading.read}
            closeModal={() => setUpdateModal(false)}
            handleAssessmentUpdate={handleAssessmentUpdate}
            updating={assessmentConfigurationLoading.update}
            data={assessmentConfiguration}
            type="edit"
          />
        }
      />

      {/* Assessment Detail Modal */}
      <CustomModal
        isOpen={detailModal}
        toggle={() => setDetailModal((prev) => !prev)}
        size="lg"
        modalHeader={"Assessment Detail"}
        children={<AssessmentDetail detailPayload={detailPayload} />}
      />

      {/* Subject Configiration Modal */}
      <CustomModal
        size="md"
        isOpen={subjectConfigModal}
        toggle={() => setSubjectConfigModal(false)}
        modalHeader={"Subject Assessment Settings"}
        children={
          <SubjectConfiguration
            assessmentInfo={assessmentInfo}
            payload={detailPayload}
            closeModal={() => setSubjectConfigModal(false)}
          />
        }
      />

      {/* General Configiration Modal */}
      <CustomModal
        size="lg"
        isOpen={generalConfigModal}
        toggle={() => setGeneralConfigModal(false)}
        modalHeader={"General Assessment Settings"}
        children={
          <GeneralConfiguration
            assessmentInfo={assessmentInfo}
            payload={detailPayload}
            closeModal={() => setGeneralConfigModal(false)}
          />
        }
      />
    </>
  );
};

export default Assessment;
